import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useNotifications from '../../hooks/useNotifications';
import DashboardSidebar from './components/DashboardSidebar';
import OverviewSection from './components/OverviewSection';
import AutomationsSection from './components/AutomationsSection';
import CrmSection from './components/CrmSection';
import BookingSection from './components/BookingSection';
import AccountSection from './components/AccountSection';
import NotificationsSection from './components/NotificationsSection';
import MembersSection from './components/MembersSection';

const sectionTitles: Record<string, string> = {
  overview: 'Dashboard Overview',
  notifications: 'Notifications',
  members: 'Member Management',
  automations: 'My Automations',
  crm: 'CRM & Leads',
  booking: 'Book a Demo',
  settings: 'Account Settings',
};

const DashboardPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, profile, signOut, refreshProfile } = useAuth();
  const { unreadCount, markAllAsRead } = useNotifications();

  // Always re-fetch profile on dashboard load to catch any role changes
  useEffect(() => {
    refreshProfile();
  }, []);

  const displayName = profile
    ? `${profile.first_name} ${profile.last_name}`
    : user?.user_metadata?.first_name
    ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`.trim()
    : user?.email?.split('@')[0] || 'Member';

  const initials = displayName
    .split(' ')
    .map((n: string) => n[0] || '')
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleBellClick = () => {
    setActiveSection('notifications');
    setMobileMenuOpen(false);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview': return <OverviewSection onNavigate={setActiveSection} />;
      case 'notifications': return <NotificationsSection />;
      case 'members': return <MembersSection />;
      case 'automations': return <AutomationsSection />;
      case 'crm': return <CrmSection />;
      case 'booking': return <BookingSection />;
      case 'settings': return <AccountSection />;
      default: return <OverviewSection onNavigate={setActiveSection} />;
    }
  };

  return (
    <>
      <title>Members Dashboard | Everything Automation</title>
      <meta name="robots" content="noindex, nofollow" />

      <div className="flex h-screen bg-ea-black overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex shrink-0">
          <DashboardSidebar
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </div>

        {/* Mobile sidebar overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/60 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              className="absolute left-0 top-0 bottom-0 w-64"
              onClick={(e) => e.stopPropagation()}
            >
              <DashboardSidebar
                activeSection={activeSection}
                onSectionChange={(s) => { setActiveSection(s); setMobileMenuOpen(false); }}
                collapsed={false}
                onToggle={() => setMobileMenuOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top header */}
          <header className="flex items-center gap-4 px-6 h-16 border-b border-ea-dark-border bg-ea-navy/50 backdrop-blur-sm shrink-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden w-8 h-8 flex items-center justify-center text-ea-gray hover:text-white cursor-pointer"
            >
              <i className="ri-menu-3-line text-lg"></i>
            </button>

            <div className="flex items-center gap-2 text-ea-gray/50 text-xs min-w-0">
              <Link to="/" className="text-ea-gray/50 hover:text-ea-blue transition-colors whitespace-nowrap cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white/80 truncate">{sectionTitles[activeSection]}</span>
            </div>

            <div className="ml-auto flex items-center gap-3">
              <Link
                to="/"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-ea-gray hover:text-white border border-ea-dark-border hover:border-ea-blue/30 rounded-lg text-xs transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line text-xs"></i>
                Back to Site
              </Link>

              {/* Notification bell */}
              <button
                onClick={handleBellClick}
                title="Notifications"
                className={`relative w-9 h-9 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
                  activeSection === 'notifications'
                    ? 'bg-ea-blue/15 border-ea-blue/40 text-ea-blue'
                    : 'border-ea-dark-border text-ea-gray hover:text-white hover:border-ea-blue/30'
                }`}
              >
                <i className="ri-notification-3-line text-base"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-ea-blue rounded-full flex items-center justify-center">
                    <span className="text-white text-[9px] font-bold leading-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </span>
                )}
              </button>

              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ea-blue to-ea-blue-electric flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{initials}</span>
                </div>
                <div className="hidden sm:block">
                  <div className="flex items-center gap-1.5">
                    <p className="text-white text-xs font-medium whitespace-nowrap">{displayName}</p>
                    {profile?.is_admin && (
                      <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-[9px] font-bold rounded-full whitespace-nowrap">ADMIN</span>
                    )}
                  </div>
                  <p className="text-ea-gray/50 text-[10px]">{profile?.is_admin ? 'Administrator' : 'Member'}</p>
                </div>
              </div>
              <button
                onClick={signOut}
                title="Sign out"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-ea-gray hover:text-red-400 hover:bg-red-400/10 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-r-line text-sm"></i>
              </button>
            </div>
          </header>

          {/* Unread notification toast banner */}
          {unreadCount > 0 && activeSection !== 'notifications' && (
            <div
              className="flex items-center gap-3 px-6 py-2.5 bg-ea-blue/8 border-b border-ea-blue/20 cursor-pointer group"
              onClick={handleBellClick}
            >
              <div className="relative w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-notification-3-line text-ea-blue text-sm"></i>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-ea-blue rounded-full flex items-center justify-center">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping absolute"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-white relative"></span>
                </span>
              </div>
              <p className="text-ea-blue text-xs">
                You have <span className="font-bold">{unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}</span>. New automation updates available.
              </p>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-ea-blue text-xs font-medium group-hover:underline whitespace-nowrap">View all</span>
                <button
                  onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                  className="text-ea-gray/60 text-xs hover:text-ea-blue whitespace-nowrap transition-colors cursor-pointer"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Page content */}
          <main className="flex-1 overflow-y-auto p-6 lg:p-8">
            {renderSection()}
          </main>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
